import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiUpload, FiCheck, FiArrowLeft, FiFileText } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Loading from '../../ions/Loading';
import ToggleSwitch from '../../atoms/ToogleSwitch';
import FileUpload from '../../molecules/FileUpload';
import SendToSignatureModal from '../signature/SendToSignatureModal';
import CompanySignatoryBlock from './CompanySignatoryBlock';
import type { SignatorySelection } from './CompanySignatoryBlock';
import { resolveSignatoryValidity, checkRepresentationRequirement, getAllowedRolesForSignatory } from './signatorySelection';
import { contractApi } from '../../../services/contractApi';
import { contractsManagementApi } from '../../../services/contractsManagementApi';
import type { OperationResult } from '../../../services/contractsManagementApi';
import { personApi } from '../../../services/personApi';
import type { Signatory } from '../../../services/personApi';
import { signatureApi } from '../../../services/signatureApi';
import { toBase64 } from '../../../utils/fileUtils';
import { formatCpfCnpj, formatCurrency, mapOperationTypeToContractType, translateContractStatus, mergeComparisonIssues, canSendComparison, resolveSignContractType, POWER_OF_ATTORNEY_CONTRACT_TYPE } from './ContractSignPage.helpers';
import type { ComparisonField } from './ContractSignPage.helpers';
import StepIndicator from './StepIndicator';
import SigningPlanPanel from './SigningPlanPanel';
import { parseSigningPlan } from './signingPlan';
import type { SigningStep } from './signingPlan';
import { blockKeyOf, buildSignersPayload, hasSelectedWithInvalidEditedPhone, isValidSignerEmail, resolveSignerEmail } from './signerPayload';
import type { StepConfig } from './StepIndicator';
import ExtractionProgressPanel from './ExtractionProgressPanel';
import type { ExtractionProgressState } from './ExtractionProgressPanel';
import { initialExtractionProgress } from './ExtractionProgressPanel';

type Step = 'upload' | 'extraction' | 'comparison';

const SIGNING_PLAN_DEBOUNCE_MS = 400;

interface ValidationInput {
    operationData: Record<string, unknown>;
    extractedData: Record<string, unknown>;
    contractType: string;
}

const STEPS: StepConfig[] = [
    { key: 'upload', label: 'Upload', icon: <FiUpload size={14} /> },
    { key: 'extraction', label: 'Extração', icon: <FiCheck size={14} /> },
    { key: 'comparison', label: 'Validação', icon: <FiCheck size={14} /> },
];

const ContractSignPage = () => {
    const { operationId } = useParams<{ operationId: string }>();
    const navigate = useNavigate();
    const mountedRef = useRef(true);

    useEffect(() => {
        return () => { mountedRef.current = false; };
    }, []);
    const [step, setStep] = useState<Step>('upload');
    const [loading, setLoading] = useState(false);

    // Operation data (from contracts-management)
    const [operation, setOperation] = useState<OperationResult | null>(null);

    // Contract flow state
    const [file, setFile] = useState<File | null>(null);
    const [isPowerOfAttorney, setIsPowerOfAttorney] = useState(false);
    const [documentId, setDocumentId] = useState('');
    const [contractId, setContractId] = useState('');
    const [extractedData, setExtractedData] = useState<Record<string, unknown> | null>(null);
    const [iterationNumber, setIterationNumber] = useState(0);
    const [comparison, setComparison] = useState<ComparisonField[]>([]);
    const [availableRoles, setAvailableRoles] = useState<{ value: string; label: string; minCount?: number; required?: boolean; participantSource?: string }[]>([]);
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    // PF signers named in the PDF that don't resolve to a registered signatory — these block sending
    const [unresolvedPdfSigners, setUnresolvedPdfSigners] = useState<{ name: string; govTaxId: string; reason: string }[]>([]);
    const [existingContract, setExistingContract] = useState<{ contractId: string; fileName?: string; uploadedBy?: string; signatureProcessId?: string; status?: string } | null>(null);
    const [existingContracts, setExistingContracts] = useState<{ contractId: string; contractType?: string; fileName?: string; uploadedBy?: string; signatureProcessId?: string; status?: string }[]>([]);
    const [checkingExisting, setCheckingExisting] = useState(false);
    const [extractionProgress, setExtractionProgress] = useState<ExtractionProgressState>(initialExtractionProgress);

    // Company signatory blocks state (AF-2757)
    interface CompanyBlock {
        companyName: string;
        companyCnpj: string;
        participantSource: 'ASSIGNOR' | 'RECIPIENT';
        signatories: Signatory[];
        lastVerifiedAt?: string | null;
    }
    const [companyBlocks, setCompanyBlocks] = useState<CompanyBlock[]>([]);
    const [companySelections, setCompanySelections] = useState<Record<string, SignatorySelection[]>>({});
    const [allowedProducts, setAllowedProducts] = useState<string[]>([]);
    const [validationInput, setValidationInput] = useState<ValidationInput | null>(null);
    const [signingPlan, setSigningPlan] = useState<SigningStep[] | null>(null);
    const [signingPlanLoading, setSigningPlanLoading] = useState(false);

    useEffect(() => {
        if (operationId) loadOperation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operationId]);

    const loadOperation = async () => {
        try {
            const op = await contractsManagementApi.getReceivableAssignmentById(operationId!);
            if (op) {
                setOperation(op);
                checkExistingContract(op.operation_number);
            }
        } catch (e) {
            console.warn('[ContractSignPage] Erro ao carregar operação:', e);
        }
    };

    const checkExistingContract = async (operationNumber: string) => {
        setCheckingExisting(true);
        try {
            const result = await contractApi.getList({ page: 1, size: 500 });
            const contracts = result?.data || result || [];
            // Find ALL contracts for this operation that have been sent for signature
            // (exclude CREATED/READY which are incomplete uploads, and CANCELLED)
            const VISIBLE_STATUSES = ['SENT_TO_SIGNATURE', 'SIGNED', 'COMPLETED'];
            const allExisting = contracts.filter((c: any) => c.externalId === operationNumber && VISIBLE_STATUSES.includes(c.status));

            // Build the list with basic info from page-list (no N+1 calls for the list view)
            const list = allExisting.map((c: any) => ({
                contractId: c.contractId,
                contractType: c.contractType,
                fileName: c.contractType || 'Documento',
                status: c.status,
                signatureProcessId: undefined as string | undefined,
            }));
            setExistingContracts(list);

            // Enrich each contract with document name and signature process link
            const enriched = await Promise.all(list.map(async (item: typeof list[number]) => {
                try {
                    const detail = await contractApi.getContractPageForm(item.contractId);
                    const doc = detail?.documents?.[0];
                    const sigRef = detail?.signatureReferences?.[0];
                    let signatureStatus: string | undefined;
                    if (sigRef?.signatureProcessId) {
                        try {
                            const sigDetail = await signatureApi.getProcessDetail(sigRef.signatureProcessId);
                            signatureStatus = sigDetail?.status;
                        } catch { /* fallback */ }
                    }
                    return {
                        ...item,
                        fileName: doc?.fileName || item.contractType || 'Documento',
                        signatureProcessId: sigRef?.signatureProcessId,
                        status: signatureStatus || item.status,
                    };
                } catch {
                    return item;
                }
            }));
            setExistingContracts(enriched);

            // Legacy single contract (for backwards compat)
            const existing = enriched[0];
            if (existing) {
                setExistingContract({
                    contractId: existing.contractId,
                    fileName: existing.fileName,
                    signatureProcessId: existing.signatureProcessId,
                    status: existing.status,
                });
            }
        } catch (e) {
            console.warn('[ContractSignPage] Erro ao verificar contrato existente:', e);
        } finally {
            setCheckingExisting(false);
        }
    };

    // Step 1: Upload
    const handleUpload = async () => {
        if (!file) return;
        setExtractionProgress({ stage: 'uploading', fileName: file.name });
        try {
            const base64 = await toBase64(file);
            const contractType = isPowerOfAttorney ? POWER_OF_ATTORNEY_CONTRACT_TYPE : mapOperationTypeToContractType(operation?.operation_type || '');
            const docResult = await contractApi.createDocument(file.name, base64, contractType, operation?.operation_number);
            setDocumentId(docResult.documentId);
            setContractId(docResult.contractId);

            if (isPowerOfAttorney) {
                setExtractionProgress({ stage: 'done' });
                setExtractedData({});
                toast.success('Procuração enviada');
                await handleProceedToComparison({}, docResult.contractId);
                return;
            }

            // Start extraction — may return immediately (async, status=PROCESSING)
            // or may complete synchronously (legacy, status absent or COMPLETED)
            setExtractionProgress({ stage: 'extracting-text', fileName: file.name });
            const extResult = await contractApi.createExtraction(docResult.documentId, docResult.contractId);
            const extractionId = extResult.extractionId;

            // If the backend returned synchronously (legacy or already completed), fetch immediately
            setExtractionProgress({ stage: 'analyzing', fileName: file.name });
            let extraction = await contractApi.getExtractionPageForm(extractionId);

            // Poll if still processing (async backend)
            if (extraction.status === 'PROCESSING') {
                const maxAttempts = 30; // 30 * 2s = 60s max wait
                const pollInterval = 2000;

                for (let attempt = 0; attempt < maxAttempts; attempt++) {
                    await new Promise(resolve => setTimeout(resolve, pollInterval));
                    if (!mountedRef.current) return; // Component unmounted, stop polling
                    extraction = await contractApi.getExtractionPageForm(extractionId);
                    if (extraction.status !== 'PROCESSING') break;
                }
            }

            if (!mountedRef.current) return;

            if (extraction.status === 'PROCESSING') {
                setExtractionProgress({ stage: 'error', fileName: file.name, errorMessage: 'Extração demorou demais. Tente novamente em alguns instantes.' });
                return;
            }
            if (extraction.status === 'FAILED') {
                setExtractionProgress({ stage: 'error', fileName: file.name, errorMessage: 'Falha na extração dos dados do documento.' });
                return;
            }

            setExtractionProgress({ stage: 'done' });
            setExtractedData(extraction.extractedData);
            setIterationNumber(extraction.iterationNumber);
            setStep('extraction');
            toast.success('Documento enviado e dados extraídos');
        } catch (e: any) {
            setExtractionProgress({ stage: 'error', fileName: file?.name, errorMessage: e.message || 'Erro ao processar documento' });
        }
    };

    // Helper: load signatories of a legal person by CNPJ (for sending to backend)
    const loadSignatoriesByCnpj = async (cnpj: string): Promise<import('../../../services/personApi').Signatory[]> => {
        const clean = cnpj.replace(/\D/g, '');
        if (clean.length !== 14) return [];
        const legalPerson = await personApi.searchLegalPersonByCnpj(clean);
        if (!legalPerson) return [];
        // Return all signatories — inactive ones are displayed with the reason they cannot sign
        return personApi.getSignatories(legalPerson.personId);
    };

    // Step 2 → 3: Compare extracted data with operation
    const handleProceedToComparison = async (data = extractedData, targetContractId = contractId) => {
        if (!data || !targetContractId) return;
        setLoading(true);
        try {
            // Ensure operation is loaded
            let op = operation;
            if (!op && operationId) {
                op = await contractsManagementApi.getReceivableAssignmentById(operationId);
                if (op) setOperation(op);
            }

            const assignor = op?.participants?.find((p: any) => p.participant_type_name === 'ASSIGNOR' || p.participant_type_name === 'BORROWER');
            const recipient = op?.participants?.find((p: any) => p.participant_type_name === 'RECIPIENT');
            const maturityValue = op?.operation_dictionary?.total_amount ?? null;
            const disbursementValue = op?.nominal_value ?? op?.operation_dictionary?.disbursement?.totalAmount ?? null;
            const expectedContractType = mapOperationTypeToContractType(op?.operation_type || '') || op?.operation_type || '-';
            const extractedContractType = String(data.operation_type || '-');

            const fields: ComparisonField[] = isPowerOfAttorney ? [] : [
                // Nome do Cedente é apenas um aviso (AF-3649): o nome no contrato divergir do
                // cadastro é comum e inofensivo (nome fantasia, abreviação). A identidade é
                // garantida pelo CPF/CNPJ abaixo, que segue bloqueando.
                { key: 'personName', label: 'Cedente (Nome)', operationValue: assignor?.name || '-', documentValue: String(data.person_name || '-'), match: (() => { const a = (assignor?.name || '').trim().toLowerCase().replace(/\s+/g, ' '); const b = String(data.person_name || '').trim().toLowerCase().replace(/\s+/g, ' '); return a === b || a.includes(b) || b.includes(a); })(), blocking: false },
                { key: 'personGovTaxId', label: 'Cedente (CPF/CNPJ)', operationValue: formatCpfCnpj(assignor?.document), documentValue: formatCpfCnpj(String(data.person_gov_tax_id || '')), match: (assignor?.document || '').replace(/\D/g, '') === String(data.person_gov_tax_id || '').replace(/\D/g, '') },
                { key: 'recipientName', label: 'Beneficiário / Cessionária', operationValue: recipient?.name || '-', documentValue: '-', match: !!recipient?.name },
                // maturityValue and disbursementValue removed from comparison (AF-2757)
                { key: 'operationType', label: 'Tipo de Contrato', operationValue: expectedContractType.replace(/_/g, ' '), documentValue: extractedContractType.replace(/_/g, ' '), match: expectedContractType === extractedContractType, blocking: false },
            ];
            setComparison(fields);

            const extractedType = resolveSignContractType(isPowerOfAttorney, data);

            // Load signatories from Person API for both participants.
            // Inactive signatories are kept so the block can show why they cannot sign.
            const assignorSignatories = assignor?.document ? await loadSignatoriesByCnpj(assignor.document) : [];
            const recipientSignatories = recipient?.document ? await loadSignatoriesByCnpj(recipient.document) : [];

            // Load roles and allowed products from ContractType configuration
            let loadedRoles: any[] = [];
            let ctAllowedProducts: string[] = [];
            try {
                const ct = await contractApi.getContractType(extractedType);
                ctAllowedProducts = ct?.allowedProducts || [];
                if (ct?.structures?.[0]?.roles) {
                    loadedRoles = ct.structures[0].roles.map((r: any) => ({
                        value: r.role,
                        label: r.label || r.role,
                        minCount: r.minCount || 1,
                        required: r.required !== false,
                        participantSource: r.participantSource || 'ANY',
                    }));
                    setAvailableRoles(loadedRoles);
                }
            } catch { /* ContractType may not exist yet, roles will be empty */ }
            setAllowedProducts(ctAllowedProducts);

            // Cross-check the PF signers extracted from the PDF against the registered signatories.
            // Every name/CPF in the document must resolve to a signatory of one of the companies —
            // otherwise there is no email/phone to build the envelope and no firmas e poderes validation.
            const pdfPfSigners = (Array.isArray(data.signers) ? data.signers : [])
                .map((s: any) => ({
                    name: String(s.name || ''),
                    govTaxId: String(s.gov_tax_id || '').replace(/\D/g, ''),
                    role: String(s.role || ''),
                }))
                .filter((s: any) => s.govTaxId.length === 11);

            const allSignatories = [...assignorSignatories, ...recipientSignatories];
            const documentInfoByUuid: Record<string, { inDocument: boolean; pdfName?: string; nameDiverges?: boolean }> = {};
            const unresolved: { name: string; govTaxId: string; reason: string }[] = [];

            for (const pdfSigner of pdfPfSigners) {
                const match = allSignatories.find(
                    sig => (sig.socialId || '').replace(/\D/g, '') === pdfSigner.govTaxId
                );
                if (match) {
                    const personName = (match.fullName || '').trim().toLowerCase().replace(/\s+/g, ' ');
                    const pdfName = pdfSigner.name.trim().toLowerCase().replace(/\s+/g, ' ');
                    const namesMatch = personName === pdfName
                        || personName.includes(pdfName)
                        || pdfName.includes(personName);
                    documentInfoByUuid[match.individualUuid] = {
                        inDocument: true,
                        pdfName: pdfSigner.name,
                        nameDiverges: !namesMatch,
                    };
                } else {
                    // Distinguish "not registered at all" from "registered but not a signatory of the company"
                    let reason = 'Não encontrado no Cadastro — cadastre a pessoa antes de enviar';
                    try {
                        const individual = await personApi.searchIndividualByCpf(pdfSigner.govTaxId);
                        if (individual) {
                            reason = 'Cadastrado, mas não vinculado como signatário de nenhuma das empresas desta operação — vincule com firmas e poderes';
                        }
                    } catch { /* keep the default reason */ }
                    unresolved.push({ name: pdfSigner.name, govTaxId: pdfSigner.govTaxId, reason });
                }
            }
            setUnresolvedPdfSigners(unresolved);

            // Persist contract metadata (signers are persisted on confirm, from the selection)
            await contractApi.updateContract(targetContractId, {
                contractType: extractedType || undefined,
                metaData: {
                    person_name: assignor?.name || String(data.person_name || ''),
                    person_gov_tax_id: (assignor?.document || String(data.person_gov_tax_id || '')).replace(/\D/g, ''),
                    operation_value: maturityValue,
                    disbursement_value: disbursementValue,
                },
            });

            // Backend validation — only the operation vs. document comparison is used here.
            // Structure and signer validation are computed from the selection made in the blocks.
            const input: ValidationInput = {
                operationData: {
                    personName: assignor?.name || '',
                    personGovTaxId: (assignor?.document || '').replace(/\D/g, ''),
                    operationType: mapOperationTypeToContractType(op?.operation_type || '') || '',
                },
                extractedData: {
                    personName: String(data.person_name || ''),
                    personGovTaxId: String(data.person_gov_tax_id || '').replace(/\D/g, ''),
                    operationType: extractedType,
                    operationValue: data.operation_value != null ? Number(data.operation_value) || null : null,
                },
                contractType: extractedType,
            };
            setValidationInput(input);
            const validationResult = await contractApi.validateForSignature(targetContractId, { ...input, signers: [] });

            // The local list decides which rows are displayed (always all of them); the backend
            // decides which diverge and whether each divergence blocks the send.
            setComparison(mergeComparisonIssues(
                fields, validationResult.comparisonValidation?.issues || [],
            ));

            // Build the company signatory blocks — the single source of the signature payload.
            const blocks: CompanyBlock[] = [];
            const selectionsMap: Record<string, SignatorySelection[]> = {};

            const buildBlock = (
                participantSource: 'ASSIGNOR' | 'RECIPIENT',
                participant: any,
                signatories: Signatory[],
            ) => {
                if (!participant?.document || signatories.length === 0) return;
                const cnpj = (participant.document || '').replace(/\D/g, '');
                const block: CompanyBlock = {
                    companyName: participant.name || '',
                    companyCnpj: formatCpfCnpj(cnpj),
                    participantSource,
                    signatories,
                    lastVerifiedAt: signatories[0]?.lastVerifiedAt || null,
                };
                blocks.push(block);
                const blockKey = blockKeyOf(block);
                // Role pre-marked by the ContractType participantSource
                const defaultRole = loadedRoles.find((r: any) => r.participantSource === participantSource)?.value
                    || loadedRoles.find((r: any) => r.participantSource === 'ANY')?.value
                    || loadedRoles[0]?.value
                    || '';
                selectionsMap[blockKey] = signatories.map(sig => {
                    const docInfo = documentInfoByUuid[sig.individualUuid];
                    // Pre-select the best compatible role for this signatory
                    const sigRoles = getAllowedRolesForSignatory(sig, loadedRoles.map((r: any) => ({ value: r.value, label: r.label })));
                    const bestRole = sigRoles.find(r => r.value === defaultRole)?.value
                        || sigRoles[0]?.value
                        || defaultRole;
                    return {
                        signatory: sig,
                        selected: !!docInfo?.inDocument,
                        useDigitalCertificate: (sig.signAsEntries || []).some(e => e.useDigitalCertificate),
                        contractRole: bestRole,
                        inDocument: !!docInfo?.inDocument,
                        pdfName: docInfo?.pdfName,
                        nameDiverges: !!docInfo?.nameDiverges,
                    };
                });
            };

            buildBlock('ASSIGNOR', assignor, assignorSignatories as Signatory[]);
            buildBlock('RECIPIENT', recipient, recipientSignatories as Signatory[]);

            setCompanyBlocks(blocks);
            setCompanySelections(selectionsMap);

            setStep('comparison');
        } catch (e: any) {
            toast.error(e.message || 'Erro ao validar');
        } finally {
            setLoading(false);
        }
    };

    // --- AF-2757: Company block interaction handlers ---
    const handleCompanyToggleSelection = (blockKey: string, individualUuid: string) => {
        setCompanySelections(prev => {
            const updated = { ...prev };
            const list = [...(updated[blockKey] || [])];
            const idx = list.findIndex(s => s.signatory.individualUuid === individualUuid);
            if (idx >= 0) {
                list[idx] = { ...list[idx], selected: !list[idx].selected };
            }
            updated[blockKey] = list;
            return updated;
        });
    };

    const handleCompanyToggleDigitalCertificate = (blockKey: string, individualUuid: string) => {
        setCompanySelections(prev => {
            const updated = { ...prev };
            const list = [...(updated[blockKey] || [])];
            const idx = list.findIndex(s => s.signatory.individualUuid === individualUuid);
            if (idx >= 0) {
                list[idx] = { ...list[idx], useDigitalCertificate: !list[idx].useDigitalCertificate };
            }
            updated[blockKey] = list;
            return updated;
        });
    };

    const handleCompanyRoleChange = (blockKey: string, individualUuid: string, role: string) => {
        setCompanySelections(prev => {
            const updated = { ...prev };
            const list = [...(updated[blockKey] || [])];
            const idx = list.findIndex(s => s.signatory.individualUuid === individualUuid);
            if (idx >= 0) {
                list[idx] = { ...list[idx], contractRole: role };
            }
            updated[blockKey] = list;
            return updated;
        });
    };

    /** Chooses which name goes to DocuSign when the PDF and the Person record diverge */
    const handleCompanyNameSourceChange = (blockKey: string, individualUuid: string, source: 'person' | 'document') => {
        setCompanySelections(prev => {
            const updated = { ...prev };
            const list = [...(updated[blockKey] || [])];
            const idx = list.findIndex(s => s.signatory.individualUuid === individualUuid);
            if (idx >= 0) {
                list[idx] = { ...list[idx], nameSource: source };
            }
            updated[blockKey] = list;
            return updated;
        });
    };

    const handleCompanyPhoneChange = (blockKey: string, individualUuid: string, phone: string) => {
        setCompanySelections(prev => {
            const updated = { ...prev };
            const list = [...(updated[blockKey] || [])];
            const idx = list.findIndex(s => s.signatory.individualUuid === individualUuid);
            if (idx >= 0) {
                list[idx] = { ...list[idx], phoneOverride: phone };
            }
            updated[blockKey] = list;
            return updated;
        });
    };

    const handleCompanyEmailChange = (blockKey: string, individualUuid: string, email: string) => {
        setCompanySelections(prev => {
            const updated = { ...prev };
            const list = [...(updated[blockKey] || [])];
            const idx = list.findIndex(s => s.signatory.individualUuid === individualUuid);
            if (idx >= 0) {
                list[idx] = { ...list[idx], emailOverride: email };
            }
            updated[blockKey] = list;
            return updated;
        });
    };

    // Signing order of the current selection, computed by the contract (same rule as the send)
    useEffect(() => {
        if (step !== 'comparison' || !contractId || !validationInput) return;
        let cancelled = false;
        setSigningPlanLoading(true);
        const timer = setTimeout(async () => {
            try {
                const result = await contractApi.validateForSignature(contractId, {
                    ...validationInput,
                    signers: buildSignersPayload(companyBlocks, companySelections),
                });
                if (!cancelled && mountedRef.current) setSigningPlan(parseSigningPlan(result));
            } catch {
                if (!cancelled && mountedRef.current) setSigningPlan(null);
            } finally {
                if (!cancelled && mountedRef.current) setSigningPlanLoading(false);
            }
        }, SIGNING_PLAN_DEBOUNCE_MS);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [step, contractId, validationInput, companyBlocks, companySelections]);

    // Step 3: Confirm and open signature modal
    const handleConfirmAndSend = async () => {
        if (!contractId) return;
        setLoading(true);
        try {
            const selectedSigners = buildSignersPayload(companyBlocks, companySelections);

            const contractType = resolveSignContractType(isPowerOfAttorney, extractedData);
            const result = await contractApi.updateContract(contractId, {
                signers: selectedSigners,
                contractType: contractType || undefined,
            });

            // Backend has the final word on the structure
            if (result.validation && !result.validation.canSendToSignature) {
                const issues = (result.validation.issues || []).map((i: any) => i.message).join('; ');
                toast.warn(issues || 'Estrutura do contrato ainda incompleta');
                setLoading(false);
                return;
            }

            setShowSignatureModal(true);
        } catch (e: any) {
            toast.error(e.message || 'Erro ao confirmar');
        } finally {
            setLoading(false);
        }
    };

    // --- Validation state, derived entirely from the company block selections ---
    const steps = isPowerOfAttorney ? STEPS.filter(s => s.key !== 'extraction') : STEPS;
    const stepIndex = steps.findIndex(s => s.key === step);
    // Use the value extracted from the PDF (valor no vencimento) for proxy validation.
    // This is the authoritative contract value — the operation's total_amount is only a reference.
    // Coerce to number: extractedData values may arrive as strings from the JSONB.
    const extractedValue = extractedData?.operation_value != null ? Number(extractedData.operation_value) : null;
    const contractValue = (extractedValue && !isNaN(extractedValue) ? extractedValue : null)
        ?? operation?.operation_dictionary?.total_amount ?? null;

    const allSelectedFromBlocks = Object.values(companySelections).flat().filter(s => s.selected);
    const hasSelectedWithoutEmail = allSelectedFromBlocks.some(s => !isValidSignerEmail(resolveSignerEmail(s)));
    const hasSelectedWithInvalidPhone = hasSelectedWithInvalidEditedPhone(allSelectedFromBlocks);
    const hasSelectedWithoutRole = allSelectedFromBlocks.some(s => !s.contractRole);
    const hasInvalidSelected = allSelectedFromBlocks.some(
        s => !resolveSignatoryValidity(s.signatory, contractValue, allowedProducts).valid
    );

    // Representation format requirement, per company
    const hasRepresentationIssue = companyBlocks.some(block => {
        const blockKey = blockKeyOf(block);
        const selectedSigs = (companySelections[blockKey] || []).filter(s => s.selected).map(s => s.signatory);
        if (selectedSigs.length === 0) return false;
        return checkRepresentationRequirement(selectedSigs, allowedProducts) !== null;
    });

    // Contract structure: minCount per required role, counted over the selected signatories.
    // Each role is counted independently — Testemunha and Validador are distinct.
    // Source-specific requirements only count signers from that source's block.
    const structureIssues = useMemo(() => {
        if (availableRoles.length === 0) return [];
        // Count selections by (role, source)
        const roleSourceCounts: Record<string, number> = {}; // "ROLE|SOURCE" → count
        const roleAnyCounts: Record<string, number> = {};    // "ROLE" → total regardless of source
        for (const block of companyBlocks) {
            const blockKey = blockKeyOf(block);
            const selections = companySelections[blockKey] || [];
            for (const sel of selections) {
                if (!sel.selected || !sel.contractRole) continue;
                const key = `${sel.contractRole}|${block.participantSource}`;
                roleSourceCounts[key] = (roleSourceCounts[key] || 0) + 1;
                roleAnyCounts[sel.contractRole] = (roleAnyCounts[sel.contractRole] || 0) + 1;
            }
        }
        const issues: { message: string }[] = [];
        availableRoles.forEach(req => {
            if (req.required === false) return;
            const min = req.minCount || 1;
            let count: number;
            if (req.participantSource === 'ANY') {
                count = roleAnyCounts[req.value] || 0;
            } else {
                // Source-specific: count from that source + signers without explicit source (retrocompat)
                count = (roleSourceCounts[`${req.value}|${req.participantSource}`] || 0)
                    + (roleSourceCounts[`${req.value}|ANY`] || 0);
            }
            if (count < min) {
                issues.push({ message: `${req.label}: necessário ${min}, selecionado ${count}` });
            }
        });
        return issues;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableRoles, companySelections, companyBlocks]);

    const allComparisonFieldsMatch = canSendComparison(comparison, isPowerOfAttorney);
    const canSend = allSelectedFromBlocks.length > 0
        && allComparisonFieldsMatch
        && structureIssues.length === 0
        && unresolvedPdfSigners.length === 0
        && !hasSelectedWithoutEmail
        && !hasSelectedWithInvalidPhone
        && !hasSelectedWithoutRole
        && !hasInvalidSelected
        && !hasRepresentationIssue;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-800">
            {loading && <Loading />}

            {/* Header */}
            <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-6 py-4">
                <div className="flex items-center gap-3 max-w-5xl mx-auto">
                    <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">
                        <FiArrowLeft className="text-gray-600 dark:text-gray-400" />
                    </button>
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Enviar Contrato para Assinatura</h1>
                        {operation && <p className="text-sm text-gray-500">Operação: {operation.sequencial_number ?? ''} - {operation.participants?.find((p: any) => p.participant_type_name === 'ASSIGNOR' || p.participant_type_name === 'BORROWER')?.name ?? ''}</p>}
                    </div>
                </div>
            </div>

            {/* Steps */}
            <div className="max-w-5xl mx-auto px-6 py-4">
                <StepIndicator steps={steps} currentIndex={stepIndex} />
            </div>

            <div className="max-w-5xl mx-auto px-6 pb-8">
                {/* Step 1: Upload */}
                {step === 'upload' && (
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Upload do Documento</h2>

                        {/* Operation contract type info */}
                        {operation && (
                            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                <p className="text-xs text-blue-600 dark:text-blue-400">
                                    📋 Tipo de contrato esperado: <span className="font-semibold">{(mapOperationTypeToContractType(operation.operation_type || '') || operation.operation_type || '-').replace(/_/g, ' ')}</span>
                                </p>
                            </div>
                        )}

                        {/* Existing contracts for this operation */}
                        {existingContracts.length > 0 && (
                            <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                                        ⚠️ Esta operação já possui {existingContracts.length} contrato(s) enviado(s)
                                    </p>
                                    <button
                                        onClick={() => navigate('/signature')}
                                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
                                    >
                                        Ver assinaturas →
                                    </button>
                                </div>
                                <div className="mt-2 space-y-2">
                                    {existingContracts.map((ec) => (
                                        <div key={ec.contractId} className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-3 border-t border-amber-200 dark:border-amber-700 pt-2 first:border-t-0 first:pt-0">
                                            <span className="truncate max-w-[200px]">📄 {ec.fileName || 'Documento'}</span>
                                            {ec.contractType && <span className="text-amber-500">({ec.contractType.replace(/_/g, ' ')})</span>}
                                            <span className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-800 text-amber-800 dark:text-amber-200 font-medium">{translateContractStatus(ec.status)}</span>
                                            {ec.signatureProcessId && (
                                                <button
                                                    onClick={() => navigate(`/signature/${ec.signatureProcessId}`)}
                                                    className="text-blue-600 dark:text-blue-400 hover:underline font-medium cursor-pointer bg-transparent border-none p-0"
                                                >
                                                    Ver processo
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Legacy single-contract display for backwards compat during transition */}
                        {existingContract && existingContracts.length === 0 && (
                            <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                                    ⚠️ Esta operação já possui um contrato enviado
                                </p>
                                <div className="mt-2 text-xs text-amber-600 dark:text-amber-400 space-y-1">
                                    <p>📄 Arquivo: <span className="font-medium">{existingContract.fileName}</span></p>
                                    {existingContract.uploadedBy && <p>👤 Enviado por: <span className="font-medium">{existingContract.uploadedBy}</span></p>}
                                    <p>📋 Status: <span className="font-medium">{translateContractStatus(existingContract.status)}</span></p>
                                </div>
                                {existingContract.signatureProcessId && (
                                    <button
                                        onClick={() => navigate(`/signature/${existingContract.signatureProcessId}`)}
                                        className="inline-block mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium cursor-pointer bg-transparent border-none p-0"
                                    >
                                        🔗 Ver processo de assinatura
                                    </button>
                                )}
                            </div>
                        )}

                        {checkingExisting && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">⏳ Verificando contratos anteriores...</p>
                        )}

                        <FileUpload id="contract-sign-pdf" name="document" label="Arquivo PDF:" accept=".pdf" info="Selecione o contrato para enviar" errorMessage="" onUploadFile={setFile} onRemoveFile={() => setFile(null)} />

                        <div className={`mt-4 flex items-start gap-4 rounded-lg border p-4 transition-colors ${isPowerOfAttorney ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20' : 'border-gray-200 bg-gray-50 dark:border-slate-700 dark:bg-slate-800/50'}`}>
                            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${isPowerOfAttorney ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-gray-200 text-gray-500 dark:bg-slate-700 dark:text-gray-400'}`}>
                                <FiFileText size={18} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-gray-900 dark:text-white">Este documento é uma procuração</p>
                                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                    Sobe sem extração e sem comparação com a operação. Vale a estrutura do tipo Procuração: devedor do cliente e validador da Agrolend.
                                </p>
                            </div>
                            <ToggleSwitch
                                checked={isPowerOfAttorney}
                                onChange={setIsPowerOfAttorney}
                                disabled={extractionProgress.stage !== 'idle'}
                                ariaLabel="É procuração"
                                className="mt-1 shrink-0"
                            />
                        </div>

                        {/* Extraction progress indicator */}
                        {extractionProgress.stage !== 'idle' && extractionProgress.stage !== 'done' && (
                            <div className="mt-4">
                                <ExtractionProgressPanel progress={extractionProgress} />
                            </div>
                        )}

                        {extractionProgress.stage === 'idle' && (
                            <button onClick={handleUpload} disabled={!file || loading || checkingExisting} className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                                {isPowerOfAttorney ? 'Enviar Procuração' : 'Enviar e Extrair Dados'}
                            </button>
                        )}
                    </div>
                )}

                {/* Step 2: Extraction result */}
                {step === 'extraction' && extractedData && (
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Dados Extraídos do Documento</h2>

                        <div className="space-y-2 mb-6">
                            {[
                                { label: 'Emitente / Devedor', value: String(extractedData.person_name || '-'), alwaysShow: true },
                                { label: 'CPF / CNPJ do Emitente', value: formatCpfCnpj(String(extractedData.person_gov_tax_id || '')), alwaysShow: true },
                                { label: 'Tipo de Contrato', value: String(extractedData.operation_type || '-').replace(/_/g, ' '), alwaysShow: true },
                                { label: 'Nº do Contrato', value: String(extractedData.contract_number || '-'), alwaysShow: false },
                                { label: 'Data de Emissão', value: String(extractedData.issue_date || 'Não informada'), alwaysShow: true },
                                { label: 'Data de Vencimento', value: String(extractedData.due_date || 'Não informada'), alwaysShow: true },
                                { label: 'Valor no Vencimento', value: formatCurrency(extractedData.operation_value as number), alwaysShow: true },
                                { label: 'Valor de Desembolso', value: formatCurrency(extractedData.disbursement_value as number), alwaysShow: true },
                                { label: 'Taxa de Juros (% a.a.)', value: extractedData.interest_rate ? `${extractedData.interest_rate}%` : '-', alwaysShow: false },
                                { label: 'Indexador', value: String(extractedData.indexer || '-'), alwaysShow: false },
                                { label: 'Credor / Cessionária', value: String(extractedData.creditor_name || '-'), alwaysShow: false },
                                { label: 'CNPJ do Credor', value: extractedData.creditor_gov_tax_id ? formatCpfCnpj(String(extractedData.creditor_gov_tax_id)) : '-', alwaysShow: false },
                                { label: 'Produto (Safra)', value: String(extractedData.crop_description || '-'), alwaysShow: false },
                                { label: 'Quantidade (ton)', value: extractedData.crop_quantity_tons ? String(extractedData.crop_quantity_tons) : '-', alwaysShow: false },
                                { label: 'Garantia', value: String(extractedData.guarantee_description || '-'), alwaysShow: false },
                            ].filter(f => f.alwaysShow || (f.value !== '-' && f.value !== '')).map((f, i) => (
                                <div key={i} className="p-3 rounded-lg border border-gray-200 dark:border-slate-700">
                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{f.label}</span>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{f.value}</p>
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-3">
                            <button onClick={() => setStep('upload')} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-700 dark:text-gray-300">Voltar</button>
                            <button onClick={() => handleProceedToComparison()} disabled={loading} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                                {loading ? 'Validando...' : 'Validar e Comparar'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 3: Comparison + Validation */}
                {step === 'comparison' && (
                    <div className="space-y-4">
                        {/* Data comparison */}
                        {!isPowerOfAttorney && (
                        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Comparação: Operação vs. Documento</h2>
                            <div className="space-y-2">
                                {comparison.map((field, i) => (
                                    <div key={i} className={`p-3 rounded-lg border ${field.match ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20' : field.blocking === false ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20' : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'}`}>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span>{field.match ? '✅' : field.blocking === false ? '⚠️' : '❌'}</span>
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{field.label}</span>
                                        </div>
                                        {field.match ? (
                                            <div className="ml-7 text-xs">
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {(field.operationValue && field.operationValue !== '-') ? field.operationValue : field.documentValue}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="ml-7 text-xs space-y-0.5">
                                                <p className="text-gray-600 dark:text-gray-400">Operação: <span className="font-medium">{field.operationValue}</span></p>
                                                <p className="text-red-600 dark:text-red-400">Documento: <span className="font-medium">{field.documentValue}</span></p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        )}

                        {/* AF-2757: Company signatory selection blocks */}
                        {companyBlocks.length > 0 && (
                            <div className="space-y-4">
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Seleção de Signatários por Empresa</h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400 -mt-2">
                                    Selecione os signatários que devem assinar este contrato. Apenas os selecionados serão incluídos no envelope.
                                </p>
                                {companyBlocks.map(block => {
                                    const blockKey = blockKeyOf(block);
                                    const roleOptions = availableRoles
                                        .filter(r => r.participantSource === block.participantSource || r.participantSource === 'ANY')
                                        .map(r => ({ value: r.value, label: r.label }));
                                    const defaultRoleForSource = availableRoles.find(r => r.participantSource === block.participantSource)?.value || availableRoles[0]?.value || '';
                                    return (
                                        <CompanySignatoryBlock
                                            key={blockKey}
                                            companyName={block.companyName}
                                            companyCnpj={block.companyCnpj}
                                            participantSource={block.participantSource}
                                            signatories={block.signatories}
                                            selections={companySelections[blockKey] || []}
                                            contractValue={contractValue}
                                            allowedProducts={allowedProducts}
                                            availableRoles={roleOptions}
                                            defaultRole={defaultRoleForSource}
                                            lastVerifiedAt={block.lastVerifiedAt}
                                            onToggleSelection={(uuid) => handleCompanyToggleSelection(blockKey, uuid)}
                                            onToggleDigitalCertificate={(uuid) => handleCompanyToggleDigitalCertificate(blockKey, uuid)}
                                            onRoleChange={(uuid, role) => handleCompanyRoleChange(blockKey, uuid, role)}
                                            onNameSourceChange={(uuid, source) => handleCompanyNameSourceChange(blockKey, uuid, source)}
                                            onEmailChange={(uuid, email) => handleCompanyEmailChange(blockKey, uuid, email)}
                                            onPhoneChange={(uuid, phone) => handleCompanyPhoneChange(blockKey, uuid, phone)}
                                        />
                                    );
                                })}
                            </div>
                        )}

                        {companyBlocks.length > 0 && (
                            <SigningPlanPanel plan={signingPlan} loading={signingPlanLoading} />
                        )}

                        {/* Signers validation (PDF-extracted: testemunhas, validadores) */}
                        {/* PF named in the PDF that don't resolve to a registered signatory — blocks sending */}
                        {unresolvedPdfSigners.length > 0 && (
                            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                                    ⛔ Pessoas citadas no documento sem cadastro de signatário — o envio está bloqueado:
                                </p>
                                <div className="mt-2 space-y-1.5">
                                    {unresolvedPdfSigners.map((s, i) => (
                                        <div key={i} className="ml-4 text-xs">
                                            <p className="font-medium text-red-700 dark:text-red-300">
                                                {s.name} — {formatCpfCnpj(s.govTaxId)}
                                            </p>
                                            <p className="text-red-600 dark:text-red-400">{s.reason}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Contract structure validation, computed from the selection */}
                        {structureIssues.length > 0 && (
                            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <p className="text-sm text-red-700 dark:text-red-300 font-medium">⚠️ Estrutura do contrato incompleta:</p>
                                {structureIssues.map((issue, i) => (
                                    <p key={i} className="text-xs text-red-600 dark:text-red-400 ml-4 mt-1">{issue.message}</p>
                                ))}
                            </div>
                        )}

                        {hasSelectedWithoutRole && (
                            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                                    ⛔ Há signatário(s) selecionado(s) sem papel definido. Selecione o papel de cada signatário para continuar.
                                </p>
                            </div>
                        )}

                        {allSelectedFromBlocks.length === 0 && companyBlocks.length > 0 && (
                            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">
                                    ⚠️ Nenhum signatário selecionado. Selecione quem deve assinar nos blocos acima.
                                </p>
                            </div>
                        )}

                        {companyBlocks.length === 0 && (
                            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                                    ⛔ Nenhuma empresa participante possui signatários cadastrados no Person. Cadastre os signatários antes de enviar.
                                </p>
                            </div>
                        )}

                        {!allComparisonFieldsMatch && comparison.length > 0 && (
                            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <p className="text-sm text-red-700 dark:text-red-300 font-medium">⛔ Divergências bloqueantes na comparação:</p>
                                {comparison.filter(f => !f.match && f.blocking !== false).map((f, i) => (
                                    <p key={i} className="text-xs text-red-600 dark:text-red-400 ml-4 mt-1">{f.label}: Operação ({f.operationValue}) ≠ Documento ({f.documentValue})</p>
                                ))}
                            </div>
                        )}

                        {comparison.some(f => !f.match && f.blocking === false) && (
                            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">⚠️ Divergências informativas (não bloqueiam o envio):</p>
                                {comparison.filter(f => !f.match && f.blocking === false).map((f, i) => (
                                    <p key={i} className="text-xs text-amber-600 dark:text-amber-400 ml-4 mt-1">{f.label}: Operação ({f.operationValue}) ≠ Documento ({f.documentValue})</p>
                                ))}
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button onClick={() => setStep(isPowerOfAttorney ? 'upload' : 'extraction')} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-700 dark:text-gray-300">Voltar</button>
                            <button onClick={handleConfirmAndSend} disabled={!canSend}
                                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                                Confirmar e Enviar para Assinatura
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Signature Modal (opens over comparison step) */}
            {showSignatureModal && (
                <SendToSignatureModal
                    contractId={contractId}
                    operationNumber={operation?.operation_number || ''}
                    fileName={file?.name || ''}
                    contractType={String(extractedData?.operation_type || '')}
                    operationCreatedBy={operation?.created_by}
                    onClose={() => { setShowSignatureModal(false); }}
                    onSuccess={(processId) => {
                        setShowSignatureModal(false);
                        toast.success('Enviado para assinatura!');
                        navigate(`/signature/${processId}`);
                    }}
                />
            )}
        </div>
    );
};

export default ContractSignPage;
